import { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";

/* =============== DATA =============== */

const REASONS = [
  {
    icon: "🌸",
    title: "Il tuo sorriso",
    text: "Il tuo sorriso ha un effetto strano e magico: fa sembrare tutto più leggero, anche le giornate storte.",
  },
  {
    icon: "✨",
    title: "I tuoi occhi",
    text: "I tuoi occhi hanno qualcosa che ti fa fermare a guardarli. Sembrano sorridere insieme a te, anche quando non parli.",
  },
  {
    icon: "💗",
    title: "Il tuo cuore immenso",
    text: "Hai un cuore enorme, che fa spazio a tutti e a tutto.",
  },
  {
    icon: "🌷",
    title: "La tua dolcezza",
    text: "C'è una dolcezza nei tuoi gesti piccoli che non si trova facilmente. E rende ogni momento più bello.",
  },
  {
    icon: "🦋",
    title: "Il tuo modo di esserci",
    text: "Quando ci sei, ci sei davvero. Ascolti, ricordi, ti accorgi delle cose. È un dono raro.",
  },
];

const STITCH_MSGS = [
  "Ciao Maria! 💙",
  "Ohana! 🌺",
  "Sei speciale!",
  "Stitch ti vuole bene 💜",
  "Continua a sorridere ✨",
  "626 = forte come te!",
  "Aloha, Maria 🌴",
  "Sei un piccolo esperimento perfetto",
];

const HK_IMAGES = [
  "/assets/hk1.png",
  "/assets/hk2.png",
  "/assets/hk3.png",
  "/assets/hk4.png",
  "/assets/hk5.png",
  "/assets/hk6.png",
  "/assets/hk7.png",
  "/assets/hk8.png",
];

const PAGES = [
  { id: "home", label: "Inizio" },
  { id: "reasons", label: "Perché sei fantastica" },
  { id: "stitch", label: "Acchiappa Stitch" },
  { id: "memory", label: "Memory Hello Kitty" },
  { id: "finale", label: "Dedica" },
];

/* =============== SPARKLE LAYER =============== */

function SparkleLayer() {
  const items = useMemo(() => {
    const symbols = ["✦", "✧", "★", "✿", "❀", "♡", "✩", "˖"];
    return Array.from({ length: 22 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 12,
      dur: 10 + Math.random() * 14,
      size: 10 + Math.random() * 14,
      sym: symbols[i % symbols.length],
      color: ["#ff9ec7", "#c8a8ff", "#a9d7ff", "#ffd687"][i % 4],
    }));
  }, []);

  return (
    <div className="sparkle-layer" aria-hidden="true">
      {items.map((s) => (
        <span
          key={s.id}
          className="sparkle"
          style={{
            left: `${s.left}%`,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.dur}s`,
            fontSize: `${s.size}px`,
            color: s.color,
          }}
        >
          {s.sym}
        </span>
      ))}
    </div>
  );
}

/* =============== NAV =============== */

function Nav({ current, onGo }) {
  return (
    <nav className="nav" data-testid="main-nav">
      {PAGES.map((p) => (
        <button
          key={p.id}
          className={`nav-link ${current === p.id ? "active" : ""}`}
          onClick={() => onGo(p.id)}
          data-testid={`nav-${p.id}`}
        >
          {p.label}
        </button>
      ))}
    </nav>
  );
}

function PageFooter({ current, onGo }) {
  const idx = PAGES.findIndex((p) => p.id === current);
  const prev = idx > 0 ? PAGES[idx - 1] : null;
  const next = idx < PAGES.length - 1 ? PAGES[idx + 1] : null;
  return (
    <div className="page-foot">
      {prev ? (
        <button className="btn-soft" onClick={() => onGo(prev.id)} data-testid="footer-prev">
          ← {prev.label}
        </button>
      ) : (
        <span />
      )}
      {next ? (
        <button className="btn-cute" onClick={() => onGo(next.id)} data-testid="footer-next">
          {next.label} →
        </button>
      ) : (
        <span />
      )}
    </div>
  );
}

/* =============== HERO =============== */

function Hero({ onEnter }) {
  return (
    <section className="page hero" data-testid="hero-section">
      <h1 data-testid="hero-title">
        Un piccolo posto speciale
        <br /> per un'amica fantastica <span className="heart-dot">♡</span>
      </h1>
      <button className="btn-cute btn-lg" onClick={onEnter} data-testid="hero-enter-btn">
        Entra
      </button>
    </section>
  );
}

/* =============== REASONS =============== */

function ReasonsSection({ onNext, onPrev, goTo }) {
  const [open, setOpen] = useState(null);
  return (
    <section className="page" data-testid="reasons-section">
      <h2 className="section-title">Perché sei fantastica</h2>
      <p className="section-sub">Sei tante cose belle. Queste sono solo alcune.</p>

      <div className="reason-grid">
        {REASONS.map((r, i) => (
          <button
            key={i}
            className="reason-card"
            style={{ textAlign: "left" }}
            onClick={() => setOpen(i)}
            data-testid={`reason-card-${i}`}
          >
            <div className="icon">{r.icon}</div>
            <h3>{r.title}</h3>
            <p>{r.text}</p>
          </button>
        ))}
      </div>

      {open !== null && (
        <div className="modal-overlay" onClick={() => setOpen(null)} data-testid="reason-modal">
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setOpen(null)} aria-label="Chiudi" data-testid="reason-modal-close">
              ✕
            </button>
            <div style={{ fontSize: "2.4rem" }}>{REASONS[open].icon}</div>
            <h3 className="font-display" style={{ margin: "10px 0 8px", fontSize: "1.4rem", color: "var(--ink)" }}>
              {REASONS[open].title}
            </h3>
            <p className="font-script" style={{ fontSize: "1.25rem", lineHeight: 1.5, color: "var(--rose-text)", margin: 0 }}>
              {REASONS[open].text}
            </p>
          </div>
        </div>
      )}

      <PageFooter current="reasons" onGo={goTo} />
    </section>
  );
}

/* =============== STITCH GAME =============== */

function StitchGame({ goTo }) {
  const stageRef = useRef(null);
  const [pos, setPos] = useState({ x: 40, y: 40 });
  const [score, setScore] = useState(0);
  const [msg, setMsg] = useState(null);
  const msgTimer = useRef(null);

  const move = () => {
    const st = stageRef.current;
    if (!st) return;
    const rect = st.getBoundingClientRect();
    const size = 100;
    const x = Math.random() * Math.max(0, rect.width - size);
    const y = Math.random() * Math.max(0, rect.height - size);
    setPos({ x, y });
  };

  useEffect(() => {
    const t = setInterval(move, 1300);
    return () => clearInterval(t);
  }, []);

  const handleClick = () => {
    setScore((s) => s + 1);
    const m = STITCH_MSGS[Math.floor(Math.random() * STITCH_MSGS.length)];
    setMsg(m);
    clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setMsg(null), 1400);
    move();
  };

  return (
    <section className="page" data-testid="stitch-section">
      <h2 className="section-title">Acchiappa Stitch!</h2>
      <p className="section-sub">È scappato dal laboratorio. Cliccalo prima che sparisca.</p>

      <div className="stitch-stage" ref={stageRef} data-testid="stitch-stage">
        <div className="clouds" />
        <div className="stitch-score" data-testid="stitch-score">punti: {score}</div>
        <button
          className="stitch-img"
          style={{ left: pos.x, top: pos.y }}
          onClick={handleClick}
          data-testid="stitch-target"
          aria-label="Acchiappa Stitch"
        >
          <img src="/assets/stitch.png" alt="Stitch" draggable={false} />
        </button>
        {msg && <div className="stitch-msg" data-testid="stitch-msg">{msg}</div>}
      </div>

      <PageFooter current="stitch" onGo={goTo} />
    </section>
  );
}

/* =============== HELLO KITTY MEMORY =============== */

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildDeck() {
  const pairs = HK_IMAGES.flatMap((src, i) => [
    { id: `${i}-a`, src, key: i },
    { id: `${i}-b`, src, key: i },
  ]);
  return shuffle(pairs);
}

function HelloKittyGame({ goTo }) {
  const [deck, setDeck] = useState(buildDeck);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const lock = useRef(false);

  const reset = () => {
    setDeck(buildDeck());
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    lock.current = false;
  };

  const onFlip = (idx) => {
    if (lock.current) return;
    if (flipped.includes(idx) || matched.includes(idx)) return;
    const next = [...flipped, idx];
    setFlipped(next);
    if (next.length === 2) {
      lock.current = true;
      setMoves((m) => m + 1);
      const [a, b] = next;
      if (deck[a].key === deck[b].key) {
        setTimeout(() => {
          setMatched((p) => [...p, a, b]);
          setFlipped([]);
          lock.current = false;
        }, 500);
      } else {
        setTimeout(() => {
          setFlipped([]);
          lock.current = false;
        }, 800);
      }
    }
  };

  const won = matched.length === deck.length;

  return (
    <section className="page" data-testid="hk-section">
      <h2 className="section-title">Memory di Hello Kitty</h2>
      <p className="section-sub">Trova le coppie. Quando hai finito, c'è una piccola sorpresa per te.</p>

      <div className="hk-board" data-testid="hk-board">
        {deck.map((c, idx) => {
          const isOpen = flipped.includes(idx) || matched.includes(idx);
          return (
            <div
              key={c.id}
              className={`hk-card ${isOpen ? "flipped" : ""} ${matched.includes(idx) ? "matched" : ""}`}
              onClick={() => onFlip(idx)}
              data-testid={`hk-card-${idx}`}
              role="button"
              tabIndex={0}
            >
              <div className="hk-back">♡</div>
              <div className="hk-face">
                <img src={c.src} alt="" draggable={false} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="hk-status" data-testid="hk-status">
        mosse: {moves} · coppie: {matched.length / 2}/{HK_IMAGES.length}
      </div>

      {won && (
        <div className="hk-win" data-testid="hk-win">
          🎀 Hai trovato tutte le coppie, Maria. <br />
          Come sempre, hai messo insieme i pezzi giusti.
          <div style={{ marginTop: 16 }}>
            <button className="btn-soft" onClick={reset} data-testid="hk-restart-btn">gioca di nuovo</button>
          </div>
        </div>
      )}

      <PageFooter current="memory" onGo={goTo} />
    </section>
  );
}

/* =============== FINALE + CD CORNER =============== */

function CDCorner() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className={`cd-corner ${open ? "open" : ""}`} data-testid="cd-corner">
        {!open ? (
          <>
            <button
              className="cd cd-small"
              onClick={() => setOpen(true)}
              aria-label="Apri il CD"
              data-testid="cd-disc"
            />
            <div className="cd-hint-small">pssst... è cliccabile</div>
          </>
        ) : (
          <div className="cd-popup" data-testid="cd-player">
            <button className="modal-close" onClick={() => setOpen(false)} aria-label="Chiudi" data-testid="cd-close-btn">
              ✕
            </button>
            <iframe
              title="Tony Boy - 7 AM"
              style={{ borderRadius: 14, border: 0 }}
              src="https://open.spotify.com/embed/track/5AvN3Yze7qCyKu9mglnW2A?utm_source=generator&theme=0"
              width="100%"
              height="152"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
            />
            <div className="hidden-msg" data-testid="cd-hidden-msg">
              "Sapevo che l'avresti trovato." ✨
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Finale({ goTo }) {
  return (
    <section className="page finale" data-testid="finale-section">
      <span style={{ fontSize: "3rem" }} aria-hidden>🌷</span>
      <div className="finale-card" style={{ marginTop: 18 }}>
        <h2 data-testid="finale-title">Per te, Maria</h2>
        <p>Non so se questa pagina dirà tutto quello che meriti di sentire, ma ci prova.</p>
        <p>Hai un modo di esistere che fa stare bene chi ti sta intorno. Lo fai senza accorgertene, ed è proprio per questo che vale tanto.</p>
        <p>Quindi, ogni volta che il mondo ti sembrerà strano, opaco o troppo rumoroso, ricordati di questo piccolo posto.</p>
        <p style={{ color: "var(--rose-text)" }}>Sei rara. E pure tanto. ♡</p>
      </div>
      <CDCorner />
      <PageFooter current="finale" onGo={goTo} />
    </section>
  );
}

/* =============== APP =============== */

export default function App() {
  const [page, setPage] = useState("home");

  const goTo = (id) => {
    setPage(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("%c♡ ciao Maria — c'è un piccolo segreto anche qui ♡", "color:#b03673;font-size:14px;font-family:Caveat,cursive;");
  }, []);

  return (
    <div className="App" data-testid="app-root">
      <SparkleLayer />
      <Nav current={page} onGo={goTo} />
      <main className="page-main" key={page}>
        {page === "home" && <Hero onEnter={() => goTo("reasons")} />}
        {page === "reasons" && <ReasonsSection goTo={goTo} />}
        {page === "stitch" && <StitchGame goTo={goTo} />}
        {page === "memory" && <HelloKittyGame goTo={goTo} />}
        {page === "finale" && <Finale goTo={goTo} />}
      </main>
    </div>
  );
}
